/**
 * MobileClassSelectorBar Component
 * 
 * A class selector bar for mobile that opens upwards.
 * Shows available database classes with same functionality as desktop ClassSelectorBar.
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { ParseClass } from '../../types/AppPanel';

interface MobileClassSelectorBarProps {
  /** Available classes */
  classes?: ParseClass[];
  /** Currently selected class name */
  selectedClass?: string | null;
  /** Callback when class changes */
  onClassChange?: (className: string) => void;
  /** Callback to refresh database */
  onRefresh?: () => void;
  /** Whether database is currently refreshing */
  isRefreshing?: boolean;
}

export default function MobileClassSelectorBar({
  classes,
  selectedClass,
  onClassChange,
  onRefresh,
  isRefreshing,
}: MobileClassSelectorBarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get selected class info
  const selectedClassInfo = useMemo(() => {
    if (!classes || !selectedClass) return null;
    return classes.find(c => c.name === selectedClass);
  }, [classes, selectedClass]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle class selection
  const handleSelectClass = (className: string) => {
    setIsOpen(false);
    onClassChange?.(className);
  };

  // No classes available
  if (!classes || classes.length === 0) {
    return (
      <div className="relative w-full">
        <div className="
          flex items-center justify-between gap-2 w-full
          bg-[#1a2436] rounded-full border border-white/10
          h-[40px] px-4
        ">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {/* Database Icon */}
            <svg width="16" height="16" viewBox="0 0 26 26" fill="none" className="text-white/50 flex-shrink-0">
              <ellipse cx="13" cy="6" rx="9" ry="3" stroke="currentColor" strokeWidth="2"/>
              <path d="M4 6V13C4 14.6569 8.02944 16 13 16C17.9706 16 22 14.6569 22 13V6" stroke="currentColor" strokeWidth="2"/>
              <path d="M4 13V20C4 21.6569 8.02944 23 13 23C17.9706 23 22 21.6569 22 20V13" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <span className="text-sm text-white/50">No classes</span>
          </div>

          {/* Refresh Button */}
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={isRefreshing}
              className={`
                p-1.5 rounded-full transition-colors flex-shrink-0
                ${isRefreshing 
                  ? 'text-white/30 cursor-not-allowed' 
                  : 'text-white/50 hover:text-white/80 hover:bg-white/10'}
              `}
              title="Refresh database"
            >
              <svg 
                width="14" 
                height="14" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                className={isRefreshing ? 'animate-spin' : ''}
              >
                <path d="M23 4v6h-6M1 20v-6h6" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Dropdown that opens upward */}
      {isOpen && (
        <div className="
          absolute bottom-full left-0 right-0 mb-2 z-[9999]
          bg-[#0c121c] backdrop-blur-xl
          border border-white/10 rounded-xl
          shadow-xl shadow-black/50
          max-h-[240px] overflow-y-auto
          py-1
        ">
          {classes.map((cls) => {
            const isActive = cls.name === selectedClass;
            const isSystemClass = cls.name.startsWith('_');
            return (
              <button
                key={cls.name}
                type="button"
                onClick={() => handleSelectClass(cls.name)}
                className={`
                  w-full flex items-center justify-between gap-3
                  px-4 py-3 text-left
                  transition-colors duration-100
                  ${isActive
                    ? 'bg-[#208AEC]/15 text-white'
                    : 'text-white/80 hover:bg-white/5 hover:text-white'
                  }
                `}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {/* Small dot indicator for system classes */}
                  {isSystemClass && (
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500/80 flex-shrink-0" />
                  )}
                  <span className={`text-sm font-medium truncate ${isSystemClass ? 'text-white/70' : ''}`}>
                    {cls.name}
                  </span>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Row count */}
                  <span className="text-xs text-white/40 tabular-nums">
                    {cls.rowCount}
                  </span>
                  
                  {/* Checkmark for selected */}
                  {isActive && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-[#208AEC]">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Selector Bar */}
      <div className="
        flex items-center justify-between gap-2 w-full
        bg-[#1a2436] rounded-full border border-white/10
        h-[40px] px-4
      ">
        {/* Clickable area for dropdown */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 min-w-0 flex-1"
        >
          {/* Database Icon */}
          <svg width="16" height="16" viewBox="0 0 26 26" fill="none" className="text-white/50 flex-shrink-0">
            <ellipse cx="13" cy="6" rx="9" ry="3" stroke="currentColor" strokeWidth="2"/>
            <path d="M4 6V13C4 14.6569 8.02944 16 13 16C17.9706 16 22 14.6569 22 13V6" stroke="currentColor" strokeWidth="2"/>
            <path d="M4 13V20C4 21.6569 8.02944 23 13 23C17.9706 23 22 21.6569 22 20V13" stroke="currentColor" strokeWidth="2"/>
          </svg>

          {/* Selected Class Name */}
          <span className="text-sm text-white/80 font-medium truncate">
            {selectedClass || 'Select a class'}
          </span>

          {/* Row count badge */}
          {selectedClassInfo && (
            <span className="text-xs text-white/50 bg-white/10 px-2 py-0.5 rounded-full flex-shrink-0">
              {selectedClassInfo.rowCount}
            </span>
          )}

          {/* Chevron */}
          <svg 
            width="14" 
            height="14" 
            viewBox="0 0 24 24" 
            fill="none"
            className={`text-white/50 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          >
            <path 
              d="M6 9l6 6 6-6" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Refresh Button */}
        {onRefresh && (
          <button
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing}
            className={`
              p-1.5 rounded-full transition-colors flex-shrink-0
              ${isRefreshing 
                ? 'text-white/30 cursor-not-allowed' 
                : 'text-white/50 hover:text-white/80 hover:bg-white/10'}
            `}
            title="Refresh database"
          >
            <svg 
              width="14" 
              height="14" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              className={isRefreshing ? 'animate-spin' : ''}
            >
              <path d="M23 4v6h-6M1 20v-6h6" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

